import { useCallback, useState } from 'react'

/**
 * Row selection state với toggle behavior:
 *   - Click row chưa selected → focus (set state)
 *   - Click chính row đang selected → bỏ focus (state về null)
 *
 * Dùng cho list page có toolbar action (Edit / Print / Delete / Copy) phụ thuộc
 * `selectedId != null`. Toggle cho phép user click 2 lần để clear selection
 * mà không cần nút "Clear".
 *
 * Sử dụng `T = number` cho PK numeric, `T = string` cho PK chuỗi (contract_no,
 * grn_id...). Mọi page dùng chung pattern này — KHÔNG inline `setSelectedId` để
 * tránh quên toggle.
 *
 * @example
 *   const { selectedId, toggleSelect, clearSelection } = useRowSelection<number>()
 *   // ...
 *   <TableRow
 *     selected={row.id === selectedId}
 *     onClick={() => toggleSelect(row.id)}
 *   />
 */
export function useRowSelection<T extends number | string>(initial: T | null = null) {
  const [selectedId, setSelectedId] = useState<T | null>(initial)

  const toggleSelect = useCallback((id: T | null) => {
    if (id == null) return
    setSelectedId((prev) => (prev === id ? null : id))
  }, [])

  const clearSelection = useCallback(() => setSelectedId(null), [])

  return { selectedId, setSelectedId, toggleSelect, clearSelection }
}
